import React from "react";
import { Box, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { isDriveVideoFile } from "../../utils/driveFolder";
import { lightTheme } from "../../styles/theme";

export type NoticeComposerUploadedFile = {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size?: number;
};

export type NoticeComposerAttachmentThumbVariant = "list" | "tile";

type NoticeComposerAttachmentThumbProps = {
  file: NoticeComposerUploadedFile;
  variant?: NoticeComposerAttachmentThumbVariant;
  showPlayHint?: boolean;
};

const getExtension = (fileName: string) => {
  const parts = fileName.split(".");
  if (parts.length < 2) return "FILE";
  return parts[parts.length - 1].toUpperCase();
};

/**
 * Thumbnail for notice composer attachments (image, video, or file icon).
 */
export const NoticeComposerAttachmentThumb: React.FC<
  NoticeComposerAttachmentThumbProps
> = ({ file, variant = "list", showPlayHint = true }) => {
  const isImage = file.contentType?.startsWith("image/");
  const isVideo = isDriveVideoFile(file.contentType, file.fileName);

  const listSize = { width: 56, height: 56, borderRadius: 1.5 };
  const tileSx =
    variant === "tile"
      ? { width: "100%", height: "100%", borderRadius: 0 }
      : listSize;

  if (isImage) {
    return (
      <Box
        component="img"
        src={file.fileUrl}
        alt={file.fileName}
        sx={{
          ...tileSx,
          objectFit: "cover",
          flexShrink: 0,
          display: "block",
          border: variant === "list" ? "1px solid" : "none",
          borderColor: "grey.200",
        }}
      />
    );
  }

  if (isVideo) {
    return (
      <Box
        sx={{
          ...tileSx,
          position: "relative",
          flexShrink: 0,
          overflow: "hidden",
          bgcolor: "common.black",
          border: variant === "list" ? "1px solid" : "none",
          borderColor: "grey.200",
        }}
      >
        <Box
          component="video"
          src={file.fileUrl}
          muted
          playsInline
          controls={variant === "tile"}
          preload="metadata"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {showPlayHint && variant === "list" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.35))",
            }}
          >
            <PlayCircleOutlineIcon
              sx={{ fontSize: 28, color: "common.white" }}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...tileSx,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        flexShrink: 0,
        bgcolor: "grey.50",
        border: variant === "list" ? "1px solid" : "none",
        borderColor: "grey.200",
      }}
    >
      <DescriptionIcon
        sx={{ fontSize: variant === "tile" ? 36 : 28, color: lightTheme.colors.materialBlue }}
      />
      {variant === "tile" && (
        <Typography
          variant="caption"
          sx={{
            px: 0.8,
            py: 0.15,
            borderRadius: 0.8,
            bgcolor: lightTheme.colors.materialBlue,
            color: "common.white",
            fontSize: "0.62rem",
            fontWeight: 700,
            maxWidth: "70%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getExtension(file.fileName)}
        </Typography>
      )}
    </Box>
  );
};

export default NoticeComposerAttachmentThumb;
